import React, { useState, useCallback, useEffect } from 'react';
import { Search, User, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';
import { Spinner } from '../../design-system';

interface UserInfo {
  user_id: string;
  email: string;
  email_verified: boolean;
  name?: string;
  phone_number?: string;
  created_at?: string;
  updated_at?: string;
}

interface UserSearchResponse {
  valid: boolean;
  user_info: UserInfo;
  message: string;
}

interface UserSearchProps {
  onUserSelect: (userInfo: UserInfo) => void;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  buttonText?: string;
  helpText?: React.ReactNode;
  clearAfterSelect?: boolean;
  clearSignal?: number;
}

const UserSearch: React.FC<UserSearchProps> = ({ 
  onUserSelect, 
  disabled = false,
  label = 'User ID or Email',
  placeholder = 'Enter user ID or email (e.g., user@example.com or 26f2f264-50b1-7068-304d)',
  buttonText = 'Select User',
  helpText,
  clearAfterSelect = true,
  clearSignal,
}) => {
  const [identifier, setIdentifier] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validatedUser, setValidatedUser] = useState<UserInfo | null>(null);
  
  const authenticatedApi = useAuthenticatedApi();

  useEffect(() => {
    setIdentifier('');
    setValidatedUser(null);
    setError(null);
  }, [clearSignal]);

  const handleUserSearch = useCallback(async () => {
    if (!identifier.trim()) {
      setError('Please enter a user ID or email address');
      return;
    }

    setLoading(true);
    setError(null);
    setValidatedUser(null);

    try {
      const response = await authenticatedApi.post<UserSearchResponse>(
        apiEndpoints.admin.impersonateCheck,
        { identifier: identifier.trim() }
      );

      if (response.valid && response.user_info) {
        setValidatedUser(response.user_info);
        setError(null);
      } else {
        setError('Invalid response from server');
      }
    } catch (err) {
      console.error('User search error:', err);
      if (err instanceof Error) {
        if (err.message.includes('not found')) {
          setError('User not found in the system');
        } else if (err.message.includes('permissions')) {
          setError('You do not have permission to perform this action');
        } else {
          setError(err.message);
        }
      } else {
        setError('Failed to validate user');
      }
      setValidatedUser(null);
    } finally {
      setLoading(false);
    }
  }, [identifier, authenticatedApi]);

  const handleSelectUser = () => {
    if (validatedUser) {
      onUserSelect(validatedUser);
      if (clearAfterSelect) {
        // Clear form after selection when explicitly enabled.
        setIdentifier('');
        setValidatedUser(null);
        setError(null);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading && !disabled) {
      handleUserSearch();
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="identifier" className="block text-sm font-medium text-ui-text-secondary mb-2">
          {label}
        </label>
        <div className="flex space-x-2">
          <div className="flex-1 relative">
            <input
              id="identifier"
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={placeholder}
              disabled={disabled || loading}
              className="w-full px-3 py-2 pl-10 border border-ui-border-primary rounded-lg bg-ui-background-secondary text-ui-text-primary placeholder-ui-text-tertiary dark:placeholder-ui-text-tertiary focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ui-text-tertiary" />
          </div>
          <button
            onClick={handleUserSearch}
            disabled={disabled || loading || !identifier.trim()}
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
          >
            {loading ? (
              <Spinner size={16} tone="current" />
            ) : (
              <Search size={16} />
            )}
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="flex items-center p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertCircle size={16} className="text-red-600 dark:text-red-400 mr-2 flex-shrink-0" />
          <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
        </div>
      )}

      {/* User Information Display */}
      {validatedUser && (
        <div className="space-y-3">
          <div className="flex items-center p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <CheckCircle size={16} className="text-green-600 dark:text-green-400 mr-2 flex-shrink-0" />
            <p className="text-green-700 dark:text-green-300 text-sm font-medium">User found and validated</p>
          </div>

          <div className="bg-ui-background-secondary border border-ui-border-primary rounded-lg p-4">
            <div className="flex items-center mb-3">
              <User size={20} className="text-blue-500 mr-3" />
              <h3 className="text-lg font-semibold text-ui-text-primary">User Information</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="font-medium text-ui-text-secondary">User ID:</span>
                <p className="text-ui-text-primary font-mono text-xs break-all">{validatedUser.user_id}</p>
              </div>
              <div>
                <span className="font-medium text-ui-text-secondary">Email:</span>
                <p className="text-ui-text-primary">{validatedUser.email}</p>
              </div>
              {validatedUser.name && (
                <div>
                  <span className="font-medium text-ui-text-secondary">Name:</span>
                  <p className="text-ui-text-primary">{validatedUser.name}</p>
                </div>
              )}
              <div>
                <span className="font-medium text-ui-text-secondary">Email Verified:</span>
                <p className="text-ui-text-primary">
                  {validatedUser.email_verified ? '✓ Yes' : '✗ No'}
                </p>
              </div>
              {validatedUser.phone_number && (
                <div>
                  <span className="font-medium text-ui-text-secondary">Phone:</span>
                  <p className="text-ui-text-primary">{validatedUser.phone_number}</p>
                </div>
              )}
              {validatedUser.created_at && (
                <div>
                  <span className="font-medium text-ui-text-secondary">Created:</span>
                  <p className="text-ui-text-primary">
                    {new Date(validatedUser.created_at).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>

            <div className="mt-4 pt-3 border-t border-ui-border-primary">
              <button
                onClick={handleSelectUser}
                disabled={disabled}
                className="w-full md:w-auto inline-flex items-center justify-center px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
              >
                {buttonText}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help Text */}
      {helpText && (
        <div className="text-sm text-ui-text-secondary space-y-1">
          {helpText}
        </div>
      )}
    </div>
  );
};

export default UserSearch;